import {Injectable} from "@angular/core";
import {Credentials} from "@app/core/credentials/domain/credentials";
import {
    DeliveryPointSelector,
    SelectDeliveryPointUseCase
} from "@app/core/delivery-point/application/select-delivery-point.use-case";
import {User} from "@app/core/user/domain/entities/user";
import {firstValueFrom, from, lastValueFrom, Observable} from "rxjs";
import {UserRemote} from "@app/core/user/domain/user.remote";
import {UserSaver} from "@app/core/user/domain/user-saver";
import {
    SendDeliveryPointSelectionUseCase
} from "@app/core/delivery-point/application/send-delivery-point-selection.use-case";
import {NotifyUserHasChangedUseCase} from "@app/core/user/application/notify-user-has-change.use-case";
import {SaveDeliveryPointUseCase} from "@app/core/delivery-point/application/save-delivery-point.use-case";
import {PromoterRepo} from "@app/core/promoter/domain/promoter-repo";

export interface LoginWithEncryptedPasswordUseCaseParams {
    credentials: Credentials;
    deliveryPointSelector: DeliveryPointSelector;
    recaptchaToken: string;
    promoter: string | null;
}

@Injectable({
    providedIn: 'root'
})
export class LoginWithEncryptedPasswordUseCase {

    constructor(
        private userRemote: UserRemote,
        private userSaver: UserSaver,
        private selectDeliveryPointUseCase: SelectDeliveryPointUseCase,
        private sendDeliveryPointSelectionUseCase: SendDeliveryPointSelectionUseCase,
        private notifyUserHasChanged: NotifyUserHasChangedUseCase,
        private saveDeliveryPointUseCase: SaveDeliveryPointUseCase,
        private promoterRepo: PromoterRepo,
    ) {

    }

    execute(params: LoginWithEncryptedPasswordUseCaseParams): Observable<User | null> {
       return from(this.loginWithEncryptedPassword(params));
    }

    private async loginWithEncryptedPassword(params: LoginWithEncryptedPasswordUseCaseParams): Promise<User | null> {
        const encryptedCredentials = params.credentials;
        const promoter = params.promoter;
        const user = await this.getUser(params.credentials, params.recaptchaToken);
        //Save the credentials temporarily for api calls that require authentication but the user is not logged in.
        await this.saveTemporalCredentials(encryptedCredentials);
        const selectedDeliveryPoint = await this.selectDeliveryPointUseCase.execute({
            credentials: encryptedCredentials,
            deliveryPointSelector: params.deliveryPointSelector
        });

        if (selectedDeliveryPoint == null) {
            return null;
        }

        if (promoter != null) {
            this.promoterRepo.save(promoter);
        }

        await this.sendDeliveryPointSelectionUseCase.execute(selectedDeliveryPoint.code, encryptedCredentials);
        await this.saveUserWithPassword(user, encryptedCredentials);
        await lastValueFrom(this.saveDeliveryPointUseCase.execute({deliveryPoint:selectedDeliveryPoint}));
        await this.notifyUserHasChanged.execute({email: user.email, deliveryPointCode: selectedDeliveryPoint.code});
        return user;
    }

    private getUser(encryptedCredentials: Credentials,recaptchaToken: string): Promise<User> {
        return firstValueFrom(this.userRemote.login(encryptedCredentials, recaptchaToken));
    }

    private saveTemporalCredentials(credentials: Credentials): Promise<void> {
        return this.userSaver.saveTemporalEncryptedCredentials(credentials);
    }

    private async saveUserWithPassword(user: User, credentials: Credentials): Promise<void> {
        return this.userSaver.saveWithEncryptedPassword(user, credentials);
    }
}
